import { Divider, Flex, Tag, Typography } from "antd";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { AiOutlineCheck } from "react-icons/ai";

import type { StepperItem } from "../types";

import { STEPPER_STATUS } from "../constants";
import s from "./Stepper.module.css";

interface Props {
  items: StepperItem[];
}

const getClassname = (status: string) => {
  return {
    [s.active]: status === STEPPER_STATUS.ACTIVE,
    [s.completed]: status === STEPPER_STATUS.COMPLETED,
    [s.inactive]: status === STEPPER_STATUS.INACTIVE,
  };
};

const Stepper = ({ items }: Props) => {
  const { t } = useTranslation(["common"]);

  return (
    <Flex vertical gap="large" className={s.stepperContainer}>
      {items.map((step, index) => {
        const classNames = getClassname(step.status);

        return (
          <Flex
            key={step.key}
            vertical
            align="flex-start"
            gap="middle"
            className={s.stepperItem}
          >
            <Flex gap="small">
              <Flex
                align="center"
                justify="center"
                className={clsx(s.stepperMarker, classNames)}
              >
                {step.status !== STEPPER_STATUS.COMPLETED ? (
                  index + 1
                ) : (
                  <AiOutlineCheck />
                )}
              </Flex>

              <Flex
                vertical
                gap="small"
                align="flex-start"
                className={s.stepperMarkerWrapper}
              >
                <Typography.Text
                  strong
                  className={clsx(s.stepTitle, {
                    [s.activeText]: step.status === STEPPER_STATUS.ACTIVE,
                  })}
                >
                  {step.title}
                </Typography.Text>

                {step?.subTitle && (
                  <Typography.Text type="secondary" className={s.subTitle}>
                    {step.subTitle}
                  </Typography.Text>
                )}

                {step.status === STEPPER_STATUS.COMPLETED && (
                  <Tag variant="outlined" color="green">
                    {t("status.complete")}
                  </Tag>
                )}
              </Flex>
            </Flex>

            <Flex gap="small" className={s.stepContent}>
              {index !== items.length - 1 && (
                <Divider
                  size="large"
                  orientation="vertical"
                  className={s.verticalLine}
                />
              )}

              {step.items && step.status !== STEPPER_STATUS.COMPLETED && (
                <Flex
                  vertical
                  align="flex-start"
                  justify="center"
                  className={s.subSteps}
                >
                  {step.items.map(sub => {
                    const classNames = getClassname(sub.status);

                    return (
                      <Flex
                        key={sub.key}
                        align="center"
                        gap="small"
                        className={s.subStep}
                      >
                        {sub.status === STEPPER_STATUS.COMPLETED ? (
                          <AiOutlineCheck
                            className={s.subMarkerCompleted}
                            data-testid="completed-submenu-icon"
                          />
                        ) : (
                          <div className={clsx(s.subMarker, classNames)} />
                        )}
                        <Typography.Text
                          className={clsx(s.subStepText, {
                            [s.activeText]:
                              sub.status === STEPPER_STATUS.ACTIVE,
                          })}
                        >
                          {sub.title}
                        </Typography.Text>
                      </Flex>
                    );
                  })}
                </Flex>
              )}
            </Flex>
          </Flex>
        );
      })}
    </Flex>
  );
};

export default Stepper;
